import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '@/context/AppContext';
import {
  ContextMenu, ContextMenuContent, ContextMenuItem,
  ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger,
  ContextMenuSeparator, ContextMenuTrigger
} from '@/components/ui/context-menu';
import {
  RefreshCw, Inbox, Send, FileText, ShieldAlert, Trash2, Loader2,
  ChevronDown, ChevronUp, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOLDER_LABELS = {
  inbox: 'Posta in arrivo', sent: 'Inviati', drafts: 'Bozze', spam: 'Spam', trash: 'Cestino',
};
const FOLDER_ICONS = { inbox: Inbox, sent: Send, drafts: FileText, spam: ShieldAlert, trash: Trash2 };

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return 'adesso';
    if (diffMin < 60) return `${diffMin} min fa`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h fa`;
    if (Math.floor(diffH / 24) === 1) return 'ieri';
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return ''; }
}

const GROUP_ORDER = [
  'Oggi', 'Ieri', 'Questa settimana', 'La settimana scorsa',
  'Questo mese', 'Il mese scorso',
];

function getDateGroup(dateStr) {
  if (!dateStr) return 'Più vecchi';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const emailDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((today - emailDay) / 86400000);

    if (diffDays === 0) return 'Oggi';
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return 'Questa settimana';
    if (diffDays < 14) return 'La settimana scorsa';

    if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear())
      return 'Questo mese';

    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    if (d.getMonth() === lm.getMonth() && d.getFullYear() === lm.getFullYear())
      return 'Il mese scorso';

    if (d.getFullYear() === now.getFullYear())
      return d.toLocaleDateString('it-IT', { month: 'long' })
              .replace(/^\w/, c => c.toUpperCase());

    return d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
             .replace(/^\w/, c => c.toUpperCase());
  } catch { return 'Più vecchi'; }
}

function readSort() {
  try { return JSON.parse(localStorage.getItem('mariani_sort')) || {}; } catch { return {}; }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function CategoryBadge({ catKey, categories }) {
  const cat = categories?.find(c => c.id === catKey);
  if (!cat) return null;
  return (
    <span
      className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
      style={{ background: cat.bg_color, color: cat.color }}
    >
      {cat.name}
    </span>
  );
}

function EmailRow({ email, accountId, folder, accountColor, onAction, categories }) {
  const navigate = useNavigate();
  const isUnread = !email.is_read;
  const domain = (() => {
    const fa = (email.from_addr || '').toLowerCase();
    return fa.includes('@') ? fa.split('@').pop() : '';
  })();

  const handleClick = () => {
    navigate(`/account/${email._accountId || accountId}/${email._folder || folder}/${email.uid}`);
  };

  const TARGET_FOLDERS = ['inbox', 'sent', 'drafts', 'spam', 'trash'].filter(f => f !== folder);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          data-testid={`email-row-${email.uid}`}
          data-email-id={email.uid}
          onClick={handleClick}
          className={`email-row flex items-center gap-3 px-5 py-3 cursor-pointer border-b ${isUnread ? 'email-row-unread' : ''}`}
          style={{ borderColor: '#F1F3F4' }}
        >
          {/* Unread dot */}
          <div className="flex-shrink-0 w-5 flex justify-center">
            {isUnread ? <span className="unread-dot" /> : <span className="read-dot" />}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {accountColor && (
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: accountColor }} />
              )}
              <span className={`text-sm truncate flex-1 ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                {email.from_name || email.from_addr || '(Mittente)'}
              </span>
              {email.category && <CategoryBadge catKey={email.category} categories={categories} />}
              <span className="text-xs text-gray-400 flex-shrink-0 ml-1">{formatDate(email.date_str)}</span>
            </div>
            <p className={`text-xs truncate mt-0.5 ${isUnread ? 'text-gray-700' : 'text-gray-500'}`}>
              {email.subject || '(Nessun oggetto)'}
            </p>
            {email.preview && (
              <p className="text-xs text-gray-400 truncate mt-0.5">{email.preview}</p>
            )}
          </div>
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent>
        <ContextMenuItem onClick={(e) => { e.stopPropagation(); onAction('toggleRead', email); }}>
          {isUnread ? 'Segna come letta' : 'Segna come da leggere'}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={(e) => { e.stopPropagation(); onAction('reply', email); }}>Rispondi</ContextMenuItem>
        <ContextMenuItem onClick={(e) => { e.stopPropagation(); onAction('forward', email); }}>Inoltra</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuSub>
          <ContextMenuSubTrigger>Categorizza</ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {(categories || []).map(cat => (
              <ContextMenuItem
                key={cat.id}
                onClick={(e) => { e.stopPropagation(); onAction('categorize', email, cat.id); }}
              >
                <span className="w-2 h-2 rounded-full mr-2 flex-shrink-0" style={{ background: cat.color }} />
                {cat.name}
                {email.category === cat.id && ' ✓'}
              </ContextMenuItem>
            ))}
            <ContextMenuSeparator />
            <ContextMenuItem onClick={(e) => { e.stopPropagation(); onAction('categorize', email, null); }}>
              Nessuna categoria
            </ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>
        {domain && (
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <span className="truncate">Categorizza dominio @{domain}</span>
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {(categories || []).map(cat => (
                <ContextMenuItem
                  key={cat.id}
                  onClick={(e) => { e.stopPropagation(); onAction('categorize-domain', email, cat.id); }}
                >
                  <span className="w-2 h-2 rounded-full mr-2 flex-shrink-0" style={{ background: cat.color }} />
                  {cat.name}
                </ContextMenuItem>
              ))}
              <ContextMenuSeparator />
              <ContextMenuItem onClick={(e) => { e.stopPropagation(); onAction('categorize-domain', email, null); }}>
                Nessuna categoria
              </ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuSub>
        )}
        <ContextMenuSub>
          <ContextMenuSubTrigger>Sposta in</ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {TARGET_FOLDERS.map(f => (
              <ContextMenuItem key={f} onClick={(e) => { e.stopPropagation(); onAction('move', email, f); }}>
                {FOLDER_LABELS[f]}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSeparator />
        <ContextMenuItem
          onClick={(e) => { e.stopPropagation(); onAction('delete', email); }}
          className="text-red-500 focus:text-red-500"
        >
          Elimina
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function DateGroupHeader({ label, count }) {
  return (
    <div
      className="sticky top-0 z-10 flex items-center gap-2 px-5 py-1.5"
      style={{ background: '#F7F4F0', borderBottom: '1px solid #EDE8E0' }}
    >
      <span
        className="text-xs font-semibold uppercase tracking-wider"
        style={{ color: '#9B8F84' }}
      >
        {label}
      </span>
      <span className="text-xs" style={{ color: '#C4BCB5' }}>
        — {count} {count === 1 ? 'email' : 'email'}
      </span>
    </div>
  );
}

// ── Sort toolbar ──────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { field: 'date',    label: 'Data' },
  { field: 'from',    label: 'Mittente' },
  { field: 'subject', label: 'Oggetto' },
];

function SortBar({ sortField, sortDir, groupByDate, onSort, onToggleGroup }) {
  return (
    <div
      data-testid="email-sort-bar"
      className="flex items-center justify-between px-4 py-1.5 border-b select-none"
      style={{ background: '#FAFAF9', borderColor: '#EDE8E0' }}
    >
      {/* Sort buttons */}
      <div className="flex items-center gap-0.5">
        <span className="text-xs mr-1" style={{ color: '#BDB5AC' }}>Ordina:</span>
        {SORT_OPTIONS.map(({ field, label }) => {
          const active = sortField === field;
          const SortIcon = active ? (sortDir === 'desc' ? ChevronDown : ChevronUp) : null;
          return (
            <button
              key={field}
              data-testid={`sort-by-${field}`}
              onClick={() => onSort(field)}
              className="flex items-center gap-0.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all"
              style={{
                background: active ? 'rgba(226,114,91,0.10)' : 'transparent',
                color: active ? '#E2725B' : '#6B7280',
              }}
            >
              {label}
              {SortIcon && <SortIcon size={11} strokeWidth={2.5} />}
            </button>
          );
        })}
      </div>

      {/* Group by date toggle */}
      <button
        data-testid="toggle-group-by-date"
        onClick={onToggleGroup}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all"
        style={{
          background: groupByDate ? 'rgba(226,114,91,0.10)' : 'transparent',
          color: groupByDate ? '#E2725B' : '#9CA3AF',
          border: `1px solid ${groupByDate ? 'rgba(226,114,91,0.30)' : '#E5E7EB'}`,
        }}
        title={groupByDate ? 'Raggruppa per data: attivo' : 'Raggruppa per data: disattivo'}
      >
        <CalendarDays size={12} strokeWidth={2} />
        Raggruppa per data
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function EmailList({ folder: propFolder, unified = false, categoryFilter = null }) {
  const { accountId, folder: paramFolder, category: paramCategory } = useParams();
  const folder = propFolder || paramFolder || 'inbox';
  const category = categoryFilter || paramCategory;

  const navigate = useNavigate();
  const { accounts, fetchEmails, markEmailRead, moveEmail, deleteEmail,
          openCompose, refreshUnreadCounts, loadCategoryCount, setCategoryEmail,
          customCategories } = useApp();

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newEmailsBadge, setNewEmailsBadge] = useState(0);

  // Persistent sort & group preferences
  // Default sempre "data più recente prima" (ordine di arrivo) — l'utente può comunque modificare
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [groupByDate, setGroupByDate] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mariani_group_date')) ?? false; } catch { return false; }
  });

  // Reset al default "data desc" ogni volta che si cambia folder/account
  useEffect(() => {
    setSortField('date');
    setSortDir('desc');
  }, [accountId, folder, unified, category]);
  useEffect(() => {
    localStorage.setItem('mariani_group_date', JSON.stringify(groupByDate));
  }, [groupByDate]);

  const handleSort = useCallback((field) => {
    if (field === sortField) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir(field === 'date' ? 'desc' : 'asc');
    }
  }, [sortField]);

  const FolderIcon = FOLDER_ICONS[folder] || Inbox;
  const catDef = customCategories.find(c => c.id === category);

  // ── Data fetching ───────────────────────────────────────────────────────────

  const loadEmails = useCallback(async (force = false) => {
    try {
      if (category) {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const { default: axios } = await import('axios');
        const res = await axios.get(`${API}/categories/emails/${category}`);
        setEmails(res.data.map(e => ({
          ...e, _accountId: e.account_id, _folder: e.folder,
          _accountColor: e.account_color, _accountEmail: e.account_email,
        })));
      } else if (unified) {
        const all = [];
        await Promise.all(accounts.map(async (acc) => {
          try {
            const data = await fetchEmails(acc.id, folder, force);
            data.forEach(e => all.push({ ...e, _accountId: acc.id, _accountColor: acc.color, _folder: folder }));
          } catch {}
        }));
        all.sort((a, b) => new Date(b.date_str) - new Date(a.date_str));
        setEmails(all);
      } else if (accountId) {
        const data = await fetchEmails(accountId, folder, force);
        setEmails(data);
      }
    } catch {
      toast.error('Errore nel caricamento delle email');
    }
  }, [category, unified, accountId, folder, accounts, fetchEmails]);

  useEffect(() => {
    setLoading(true);
    setNewEmailsBadge(0);
    loadEmails().finally(() => setLoading(false));
  }, [accountId, folder, unified, category, accounts.length, loadEmails]);

  // Auto-reload list when sidebar polling detects new mails (every 30s)
  useEffect(() => {
    const handler = () => { loadEmails(false); };
    window.addEventListener('mariani:new-mail', handler);
    return () => window.removeEventListener('mariani:new-mail', handler);
  }, [loadEmails]);

  // Auto-check new emails every 10 min (inbox single-account view only,
  // since unified/category views are refreshed by the global Sidebar polling).
  useEffect(() => {
    if (!accountId || folder !== 'inbox' || category) return;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const checkNew = async () => {
      try {
        const { default: axios } = await import('axios');
        const res = await axios.get(`${API}/sync-new/${accountId}/${folder}`);
        if (res.data.new > 0) {
          setNewEmailsBadge(n => n + res.data.new);
          await loadEmails(false);
          await refreshUnreadCounts();
        }
      } catch {}
    };
    const interval = setInterval(checkNew, 600000);
    return () => clearInterval(interval);
  }, [accountId, folder, category, loadEmails, refreshUnreadCounts]);

  // ── Sort & group ────────────────────────────────────────────────────────────

  const sortedEmails = useMemo(() => {
    return [...emails].sort((a, b) => {
      let va, vb;
      if (sortField === 'date') {
        va = new Date(a.date_str || 0).getTime();
        vb = new Date(b.date_str || 0).getTime();
      } else if (sortField === 'from') {
        va = (a.from_name || a.from_addr || '').toLowerCase();
        vb = (b.from_name || b.from_addr || '').toLowerCase();
      } else {
        va = (a.subject || '').toLowerCase();
        vb = (b.subject || '').toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [emails, sortField, sortDir]);

  const groupedSections = useMemo(() => {
    if (!groupByDate) return [{ group: null, items: sortedEmails }];

    const map = new Map();
    sortedEmails.forEach(email => {
      const g = getDateGroup(email.date_str);
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(email);
    });

    // Sort sections: known order first, then by first-email date descending
    return [...map.entries()]
      .sort(([a], [b]) => {
        const ai = GROUP_ORDER.indexOf(a);
        const bi = GROUP_ORDER.indexOf(b);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return 0;
      })
      .map(([group, items]) => ({ group, items }));
  }, [sortedEmails, groupByDate]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleRefresh = async () => {
    setRefreshing(true);
    setNewEmailsBadge(0);
    await loadEmails(true);
    await refreshUnreadCounts();
    await loadCategoryCount();
    setRefreshing(false);
    toast.success('Email aggiornate');
  };

  const handleAction = async (action, email, target = null) => {
    const aid = email._accountId || accountId;
    const fldr = email._folder || folder;
    try {
      switch (action) {
        case 'toggleRead': {
          const newRead = !email.is_read;
          await markEmailRead(aid, fldr, email.uid, newRead);
          setEmails(prev => prev.map(e =>
            e.uid === email.uid && (e._accountId || accountId) === aid
              ? { ...e, is_read: newRead ? 1 : 0 } : e
          ));
          toast.success(newRead ? 'Segnata come letta' : 'Segnata come da leggere');
          break;
        }
        case 'reply': {
          const account = accounts.find(a => a.id === aid);
          openCompose({ mode: 'reply', accountId: aid, to: email.from_addr, subject: `Re: ${email.subject || ''}`, fromEmail: account?.email });
          break;
        }
        case 'forward': {
          openCompose({ mode: 'forward', accountId: aid, subject: `Fwd: ${email.subject || ''}`, body: `\n\n--- Messaggio inoltrato ---\nDa: ${email.from_name || email.from_addr}\nOggetto: ${email.subject}\n\n` });
          break;
        }
        case 'categorize': {
          await setCategoryEmail(aid, fldr, email.uid, target);
          setEmails(prev => prev.map(e =>
            e.uid === email.uid && (e._accountId || accountId) === aid
              ? { ...e, category: target } : e
          ));
          if (category) {
            setEmails(prev => prev.filter(e => !(e.uid === email.uid && (e._accountId || accountId) === aid)));
          }
          await loadCategoryCount();
          toast.success(target ? `Categorizzata come ${customCategories.find(c => c.id === target)?.name}` : 'Categoria rimossa');
          break;
        }
        case 'categorize-domain': {
          // Estrae dominio dal mittente e applica la categoria a tutte le email di quel dominio
          const from = (email.from_addr || '').toLowerCase();
          const dom = from.includes('@') ? from.split('@').pop() : '';
          if (!dom) {
            toast.error('Impossibile estrarre il dominio dal mittente');
            break;
          }
          const res = await axios.patch(
            `${API}/emails/${aid}/${fldr}/${email.uid}/category`,
            { category: target, apply_to_domain: true }
          );
          const updated = res.data?.updated || 0;
          // Aggiorna localmente tutte le mail dello stesso dominio nella vista corrente
          setEmails(prev => prev.map(e => {
            const fa = (e.from_addr || '').toLowerCase();
            return fa.endsWith(`@${dom}`) ? { ...e, category: target } : e;
          }));
          if (category) {
            setEmails(prev => prev.filter(e => {
              const fa = (e.from_addr || '').toLowerCase();
              return !fa.endsWith(`@${dom}`);
            }));
          }
          await loadCategoryCount();
          const catName = customCategories.find(c => c.id === target)?.name;
          toast.success(
            target
              ? `${updated} email del dominio @${dom} categorizzate come "${catName}"`
              : `${updated} email del dominio @${dom}: categoria rimossa`
          );
          break;
        }
        case 'move': {
          await moveEmail(aid, fldr, email.uid, target);
          setEmails(prev => prev.filter(e => !(e.uid === email.uid && (e._accountId || accountId) === aid)));
          toast.success(`Spostata in ${FOLDER_LABELS[target] || target}`);
          break;
        }
        case 'delete': {
          await deleteEmail(aid, fldr, email.uid);
          setEmails(prev => prev.filter(e => !(e.uid === email.uid && (e._accountId || accountId) === aid)));
          toast.success('Spostata nel Cestino');
          break;
        }
        default: break;
      }
    } catch (err) {
      toast.error(`Errore: ${err.response?.data?.detail || err.message}`);
    }
  };

  const currentAccount = accounts.find(a => a.id === accountId);
  const headerTitle = category
    ? catDef?.name || category
    : (unified && folder === 'inbox') ? 'Mail in arrivo' : (FOLDER_LABELS[folder] || folder);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white">

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#F1F3F4' }}>
        <div className="flex items-center gap-3">
          {catDef
            ? <span className="w-5 h-5 rounded-full" style={{ background: catDef.color }} />
            : <FolderIcon size={18} className="text-gray-400" />
          }
          <div>
            <h1 className="font-semibold text-base text-gray-800">{headerTitle}</h1>
            {currentAccount && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-2 h-2 rounded-full" style={{ background: currentAccount.color }} />
                <span className="text-xs text-gray-500">{currentAccount.email}</span>
              </div>
            )}
            {catDef && <p className="text-xs text-gray-400 mt-0.5">Tutti gli account</p>}
          </div>
        </div>
        <button
          data-testid="email-list-refresh-btn"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all relative"
          style={{ borderColor: '#E8EAED', background: 'white' }}
        >
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
          Aggiorna
          {newEmailsBadge > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 w-4 h-4 text-white text-xs flex items-center justify-center rounded-full font-bold"
              style={{ background: '#E2725B', fontSize: '10px' }}
            >
              {newEmailsBadge > 9 ? '9+' : newEmailsBadge}
            </span>
          )}
        </button>
      </div>

      {/* ── Sort & Group toolbar ── */}
      <SortBar
        sortField={sortField}
        sortDir={sortDir}
        groupByDate={groupByDate}
        onSort={handleSort}
        onToggleGroup={() => setGroupByDate(g => !g)}
      />

      {/* ── Email list ── */}
      <div className="flex-1 overflow-y-auto" data-testid="email-list">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={22} className="animate-spin text-gray-400" />
            <span className="ml-3 text-sm text-gray-400">Caricamento...</span>
          </div>
        ) : sortedEmails.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <FolderIcon size={32} className="text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-500">{headerTitle} vuota</p>
          </div>
        ) : (
          groupedSections.map(({ group, items }) => (
            <React.Fragment key={group || '__all__'}>
              {group && <DateGroupHeader label={group} count={items.length} />}
              {items.map(email => (
                <EmailRow
                  key={`${email._accountId || accountId}-${email.uid}`}
                  email={email}
                  accountId={email._accountId || accountId}
                  folder={email._folder || folder}
                  accountColor={(unified || category) ? email._accountColor : null}
                  onAction={handleAction}
                  categories={customCategories}
                />
              ))}
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
}
